import {
  Component,
  DOCUMENT,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationSkipped,
  NavigationStart,
  RouteConfigLoadEnd,
  Router,
  Scroll,
} from '@angular/router';
import {
  ErrorMessage,
} from '@dapi/sdk';
import {
  getAmaClientSessionId,
} from '@des/application';
import {
  createDESLoggerLogServerClient,
  DESLogServerLoggerClientOptions,
  LOG_SERVER_LOGGER_CLIENT_OPTIONS,
} from '@des/services/logging';
import {
  LocationStore,
} from '@des/store/location';
import {
  MessagesStore,
  selectAllErrorMessages,
} from '@des/store/messages';
import {
  OrderChangeStore,
  selectOrderChange,
} from '@des/store/order-change';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  LangChangeEvent,
} from '@ngx-translate/core';
import {
  EventTrackState,
  selectEventTrackState,
} from '@o3r/analytics';
import {
  ApiFactoryService,
} from '@o3r/apis-manager';
import {
  PlaceholderRulesEngineActionHandler,
} from '@o3r/components/rules-engine';
import {
  ConfigurationBaseService,
  ConfigurationObserver,
  ConfigurationState,
  DynamicConfigurable,
} from '@o3r/configuration';
import {
  ConfigurationRulesEngineActionHandler,
} from '@o3r/configuration/rules-engine';
import {
  isProductionEnvironment,
  O3rComponent,
} from '@o3r/core';
import {
  DynamicContentService,
} from '@o3r/dynamic-content';
import {
  AssetRulesEngineActionHandler,
} from '@o3r/dynamic-content/rules-engine';
import {
  CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN,
  LocalizationConfiguration,
  LocalizationService,
  TextDirectionService,
} from '@o3r/localization';
import {
  LocalizationRulesEngineActionHandler,
} from '@o3r/localization/rules-engine';
import {
  LoggerService,
} from '@o3r/logger';
import {
  RegisteredItemFailureReason,
  RoutingGuardStore,
  selectRoutingGuardEntitiesBlockingReasons,
} from '@o3r/routing';
import {
  RulesEngineRunnerService,
  Ruleset,
  setRulesetsEntities,
} from '@o3r/rules-engine';
import {
  AnalyticsService,
  DIGITAL_TOUCH_POINT_TOKEN,
  DigitalTouchPointConfig,
  Flow,
} from '@refx/analytics-core';
import {
  AppTokenParametersService,
} from '@refx/booking-services';
import {
  AppBootstrapConfig,
  BOOTSTRAP_CONFIGURATION_TOKEN,
  DEFAULT_LOCALES,
  defaultLocalizationAppProp,
  FlowType,
  getDateFnsLocale,
  getMainLangFromLocale,
  isOrder,
  isOrderPaidAndConfirmed,
  storeLeaveTimestamp,
} from '@refx/shared-common';
import {
  ABTestingBridgeService,
  AccentedCharactersWarningService,
  AnalyticsUtilityService,
  AppConfigService,
  AppInitializationService,
  CookieService,
  CurrentTimeFactsService,
  DocumentTitleService,
  DynamicLocaleService,
  LazyLoaderDialogService,
  LazyLoaderService,
  LoadingStateService,
  LocationLoadingStateService,
  RouterLoadingStateService,
  SelectedDataService,
  SkeletalLoaderUtilityService,
  TagsService,
  UiStatusLoadingStateService,
} from '@refx/shared-services';
import {
  AppConfigStore,
  ClearLoadingContext,
  selectAppConfigAnimationsDisabled,
  selectFlowType,
  selectIsRewardFlow,
  selectLoadingContext,
  UiStatusStore,
  UpdateUiStatus,
} from '@refx/shared-store';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  firstValueFrom,
  Observable,
  Subscription,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  BookingRoutes,
  RewardRoutes,
  skeletalRoutes,
} from '../configuration/index';
import {
  environment,
} from '../environments';
import {
  AppPreferencesFactsService,
  PortalFactsService,
  TechnicalDetailsFactsService,
  UiStatusFactsService,
} from '../facts/index';
import {
  MetaConnectService,
} from '../services/index';
import {
  APP_CONFIG_DEFAULT,
  APP_CONFIG_ID,
  AppConfig,
} from './app.config';

@O3rComponent({
  componentType: 'Page'
})
@Component({
  selector: 'app',
  templateUrl: './app.template.html',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, DynamicConfigurable<AppConfig> {
  /**
   * Config attribute to be compliant with DynamicConfigurable interface. Not used at the moment, root component cannot have inputs.
   */
  public config: Partial<AppConfig> | undefined;

  /**
   * Configuration stream based on the stored application configuration
   */
  public config$: Observable<AppConfig>;

  /**
   * Application dynamic configuration based on the configuration service if used by the application
   */
  private readonly dynamicConfig$: ConfigurationObserver<AppConfig>;

  /**
   * List of subscriptions to unsubscribe on destroy
   */
  protected subscriptions: Subscription[] = [];

  /**
   * Boolean indicating if the animations should be disabled
   *
   * @see https://angular.io/api/animations/trigger#disabling-animations
   */
  public animationsDisabled$: Observable<boolean>;

  /**
   * Observable for isPending flag
   */
  public isPending$: Observable<boolean>;

  /**
   * Observable that emits when the application has completed loading based on the TTI (Time to Interact) computation
   */
  public loadingComplete$: Observable<boolean>;

  public showDebugPanel = false;

  public displayDebugPanel = () => import(/* webpackChunkName: "dev-tools" */ '@refx/shared-components').then((module) => module.DevtoolsPanelComponentStandalone);

  /**
   * Observable to store blocking reasons for redirection to an external url
   */
  public blockingReasons$: Observable<RegisteredItemFailureReason[]>;

  /**
   * Context for the current loading
   */
  public loadingContext$: Observable<string | undefined>;

  /**
   * Boolean to track the activation of the first route
   */
  public firstRouteActivated = false;

  /**
   * Boolean that decides if the skeletal loader should be displayed
   */
  public displaySkeletalLoader = false;

  /**
   * A subject to trigger the monitoring of the router events pertaining to the loading states
   */
  protected monitorRouterStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(BOOTSTRAP_CONFIGURATION_TOKEN) private readonly bootstrapConfig: AppBootstrapConfig,
    @Inject(CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN) public customLocalizationConfig: LocalizationConfiguration,
    @Inject(LOG_SERVER_LOGGER_CLIENT_OPTIONS) private readonly logServerLoggerClientOptions: DESLogServerLoggerClientOptions,
    @Inject(DIGITAL_TOUCH_POINT_TOKEN) private readonly digitalTouchpointData: DigitalTouchPointConfig,
    private readonly localizationService: LocalizationService,
    private readonly textDirectionService: TextDirectionService,
    private readonly router: Router,
    private readonly rulesEngine: RulesEngineRunnerService,
    private readonly placeholderRulesEngineActionHandler: PlaceholderRulesEngineActionHandler,
    private readonly localizationRulesEngineActionHandler: LocalizationRulesEngineActionHandler,
    private readonly assetRulesEngineActionHandler: AssetRulesEngineActionHandler,
    private readonly configurationRulesEngineActionHandler: ConfigurationRulesEngineActionHandler,
    private readonly currentTimeFactsService: CurrentTimeFactsService,
    private readonly store: Store<AppConfigStore & ConfigurationState & LocationStore & RoutingGuardStore & UiStatusStore & MessagesStore & OrderChangeStore>,
    public appConfigService: AppConfigService,
    private readonly configService: ConfigurationBaseService,
    private readonly analyticsService: AnalyticsService,
    private readonly abTestingBridgeService: ABTestingBridgeService,
    private readonly injector: Injector,
    private readonly appInitializationService: AppInitializationService,
    private readonly dynamicContentService: DynamicContentService,
    private readonly analyticsUtilityService: AnalyticsUtilityService,
    private readonly tokenParametersService: AppTokenParametersService,
    private readonly portalFacts: PortalFactsService,
    private readonly appPreferencesFacts: AppPreferencesFactsService,
    private readonly technicalDetailsFacts: TechnicalDetailsFactsService,
    private readonly uiStatusFacts: UiStatusFactsService,
    private readonly dynamicLocaleService: DynamicLocaleService,
    private readonly apiFactoryService: ApiFactoryService,
    private readonly loggerService: LoggerService,
    protected accentedCharactersWarningService: AccentedCharactersWarningService,
    protected lazyLoaderService: LazyLoaderService,
    private readonly lazyLoaderDialogService: LazyLoaderDialogService,
    private readonly cookieService: CookieService,
    private readonly selectedDataService: SelectedDataService,
    private readonly loadingStateService: LoadingStateService,
    public routerLoadingService: RouterLoadingStateService,
    private readonly locationLoadingService: LocationLoadingStateService,
    private readonly uiStatusLoadingService: UiStatusLoadingStateService,
    private readonly renderer: Renderer2,
    private readonly skeletalLoaderUtilityService: SkeletalLoaderUtilityService
  ) {
    this.loadingComplete$ = this.store.pipe(
      select(selectEventTrackState),
      map((state: EventTrackState) => !!state && state.isTTIComputed)
    );
    // TODO - need to move to module
    if (!environment.DEBUG_MODE && !!this.bootstrapConfig.loggingBaseUrl) {
      const desLogClient = createDESLoggerLogServerClient();
      (desLogClient as any).apiFactoryService = this.apiFactoryService;
      (desLogClient as any).config = this.logServerLoggerClientOptions;
      this.loggerService.registerClient(desLogClient);
    }
    this.setBodyLanguageDirection();
    this.injector.get(DocumentTitleService);
    this.injector.get(TagsService);
    this.injector.get(MetaConnectService);
    this.isPending$ = this.loadingStateService.shouldDisplayLoader$;

    this.appInitializationService.initialize();
    this.dynamicConfig$ = new ConfigurationObserver<AppConfig>(APP_CONFIG_ID, APP_CONFIG_DEFAULT, this.configService);
    this.config$ = this.dynamicConfig$.asObservable();

    this.isSkeletalLoaderEnabled();

    this.analyticsService.toggleShowSensitiveData(appInitializationService.shouldCollectSensitiveData());
    if (this.bootstrapConfig.downloadFallbackLanguageChunk) {
      this.localizationService.getTranslateService().setDefaultLang(defaultLocalizationAppProp.fallbackLanguage || DEFAULT_LOCALES.ENGB);
    }
    this.localizationService.configure();
    this.animationsDisabled$ = store.pipe(select(selectAppConfigAnimationsDisabled));
    this.store.dispatch(UpdateUiStatus({
      isPending: false
    }));
    this.analyticsService.setupIsProd(isProductionEnvironment(document.body.dataset));
    this.subscriptions.push(
      combineLatest([
        this.store.pipe(select(selectFlowType)),
        this.store.pipe(select(selectIsRewardFlow)),
        this.store.pipe(
          select(selectOrderChange),
          map((orderChange) => !!orderChange),
          distinctUntilChanged()
        )
      ]).subscribe(([flowType, isRewardFlow, orderChange]) => {
        let subCategory: Flow;
        let flow: string;

        switch (flowType) {
          case FlowType.mmb: {
            subCategory = orderChange ? Flow.ASC : Flow.MMB;
            flow = 'Servicing';
            break;
          }
          case FlowType.rebooking: {
            subCategory = Flow.REBOOKING;
            flow = 'Rebooking';
            break;
          }
          case FlowType.flightStatus: {
            subCategory = Flow.FLIGHT_STATUS;
            flow = 'Flight Status';
            break;
          }
          default: {
            if (isRewardFlow) {
              subCategory = Flow.REWARD;
              flow = 'Reward';
            } else {
              subCategory = Flow.BOOKING;
              flow = 'Booking';
            }
          }
        }
        this.analyticsService.setCurrentFlow(subCategory);
        this.setupAnalytics();
        this.analyticsService.configureDataLake('RefX', 'RefX',
          {
            digitalTouchpointDetails: {
              flow,
              name: 'RefX_offtheshelf',
              clientPhase: isProductionEnvironment(this.document.body.dataset) ? 'PRD' : this.bootstrapConfig.environment,
              language: getMainLangFromLocale(this.localizationService.getCurrentLanguage()).toUpperCase()
            }
          }
        );
      }),
      combineLatest([
        this.store.pipe(
          select(selectAllErrorMessages),
          filter((errors) => errors.length > 0)
        ),
        this.config$.pipe(
          map(({errorCodesRedirectingToRecovery}) => errorCodesRedirectingToRecovery),
          filter((errorCodesRedirectingToRecovery) => errorCodesRedirectingToRecovery.length > 0),
          distinctUntilChanged()
        )
      ]).subscribe(([errors, errorCodesRedirectingToRecovery]) => {
        if (errors.some((error) => error.code && errorCodesRedirectingToRecovery.includes(error.code))) {
          this.router.navigate([BookingRoutes.recovery]);
        }
      }),
      this.router.events.pipe(
        withLatestFrom(this.monitorRouterStatus$.asObservable()),
        filter(([event, shouldMonitorRouterStatus]) => shouldMonitorRouterStatus && event instanceof NavigationEnd)
      ).subscribe(() => {
        this.monitorLoadingStates();
        this.monitorRouterStatus$.next(false);
      }),
      // If time out, display Response timeout popup
      this.store.pipe(
        select(selectAllErrorMessages),
        filter((messages) => messages.some((errorMessage: ErrorMessage) => errorMessage.title.includes('Response timeout')))
      ).subscribe(() => {
        // Need the first value of the observable to emit in order for the dialog to open
        firstValueFrom(
          this.lazyLoaderDialogService.open(
            () => import('@refx/shared-components').then((module) => module.TimeOutDialogComponent),
            {
              data: {
                config: {},
                inputs: {},
                outputs: {}
              }
            }
          )
        );
      })
    );
  }

  /**
   * Set the language direction on the body element.
   * It is required to align the skeleton loader with the language direction correctly.
   */
  private setBodyLanguageDirection() {
    const language = this.localizationService.getCurrentLanguage();
    const direction = this.customLocalizationConfig.rtlLanguages.includes(language.split('-')[0]) ? 'rtl' : 'ltr';
    this.renderer.setAttribute(this.document.body, 'dir', direction);
  }

  /**
   * Checks if the empty route is upsell
   *
   * @returns boolean
   */
  private isEmptyUpsellRoute(): boolean {
    try {
      const search: string = JSON.parse(this.document.body.dataset?.post || '')?.search
        || JSON.parse(this.document.body.dataset?.query || '')?.search || '';
      const searchCriteria = JSON.parse(decodeURIComponent(search));
      return searchCriteria?.itineraries?.length <= 2;
    } catch {
      return false;
    }
  }

  /**
   * Checks if the skeletal loader should be displayed
   */
  public async isSkeletalLoaderEnabled() {
    try {
      const isEmptyUpsellRoute = this.isEmptyUpsellRoute();
      const isEnabled$: Observable<boolean> = this.skeletalLoaderUtilityService.shouldDisplayForRoute$(
        skeletalRoutes,
        [BookingRoutes.upsellComplexFlow, RewardRoutes.upsell],
        () => isEmptyUpsellRoute
      );
      this.displaySkeletalLoader = await firstValueFrom(isEnabled$);
    } catch {} finally {
      this.monitorLoadingStates(!this.displaySkeletalLoader);
    }
  }

  /**
   * Monitor the loading states of the sources required during the bootstrap of the application
   *
   * @param shouldMonitorRouterStatus boolean to indicate if the router events should be monitored
   */
  public monitorLoadingStates(shouldMonitorRouterStatus = true) {
    this.uiStatusLoadingService.monitorUiStatusStoreStatus();
    this.locationLoadingService.monitorLocationStoreStatus();
    if (shouldMonitorRouterStatus) {
      this.routerLoadingService.monitorRouterEventsStatus(
        (event: any) => !(
          event instanceof Scroll
          || event instanceof NavigationCancel
          || event instanceof RouteConfigLoadEnd
          || event instanceof GuardsCheckStart
          || event instanceof NavigationSkipped
        )
      );
    }
  }

  /**
   * Before we leave the app, if the browser does not support the Performance API, then we store a timestamp to check if we performed a refresh when we come back.
   * If the browser supports the Perf API nothing is done here.
   */
  @HostListener('window:beforeunload')
  public onPageUnload() {
    storeLeaveTimestamp();
    combineLatest([
      this.selectedDataService.selectedData$,
      this.config$.pipe(filter(({shouldEnableResumeBooking}) => !!shouldEnableResumeBooking), distinctUntilChanged())
    ]).pipe(take(1)).subscribe(([data]) => {
      if (!isOrder(data) || !isOrderPaidAndConfirmed(data)) {
        this.cookieService.filterCookies(true);
      }
    });
  }

  private setupRulesets() {
    this.rulesEngine.registerActionHandlers(this.placeholderRulesEngineActionHandler);
    this.rulesEngine.registerActionHandlers(this.localizationRulesEngineActionHandler);
    this.rulesEngine.registerActionHandlers(this.assetRulesEngineActionHandler);
    this.rulesEngine.registerActionHandlers(this.configurationRulesEngineActionHandler);
    this.appPreferencesFacts.register();
    this.portalFacts.register();
    this.uiStatusFacts.register();
    this.technicalDetailsFacts.register();
    this.abTestingBridgeService.register();
    this.currentTimeFactsService.registerFacts();

    const rulesetsFilePaths = [];
    if (this.bootstrapConfig.useGeneratedRulesets) {
      rulesetsFilePaths.push('rules/rulesets.json');
    }
    if (this.bootstrapConfig.useAssetsRulesets) {
      rulesetsFilePaths.push('assets/rules/rulesets.json');
    }

    if (rulesetsFilePaths.length > 0) {
      const rulesetsFetch$: Observable<Promise<Ruleset[]>>[] = rulesetsFilePaths.map(
        (rulesetsFilePath) => this.dynamicContentService.getContentPathStream(rulesetsFilePath).pipe(
          map(async (path: string) => {
            const resultCall = await fetch(path);
            return resultCall.ok ? (await resultCall.json()).rulesets ?? [] : [];
          })
        )
      );

      this.subscriptions.push(
        combineLatest(rulesetsFetch$).subscribe(
          async (rulesetsPromises: Promise<Ruleset[]>[]) => {
            const fetchedRulesets = await Promise.all(rulesetsPromises);
            this.store.dispatch(setRulesetsEntities({
              entities: Array.prototype.concat(...fetchedRulesets)
            }));
          }
        )
      );
    }
  }

  /**
   * Setup analytics method to set Digital Touch Point
   */
  private async setupAnalytics() {
    const guestOfficeId = await this.getOfficeIDFromStore();
    const portalFacts = this.appInitializationService.getPortalFacts();
    const countryCodeFromPortalFacts = (portalFacts || []).find((portalFact) => portalFact.key === 'countryCode');
    this.analyticsService.setupDigitalTouchPoint(
      getMainLangFromLocale(this.localizationService.getCurrentLanguage()),
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.market || this.appInitializationService.getPointOfSale() || countryCodeFromPortalFacts?.value || '',
      this.digitalTouchpointData.digitalTouchPoint?.category?.primaryCategory || 'RefX',
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.officeID || guestOfficeId,
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.sessionID || getAmaClientSessionId(),
      this.digitalTouchpointData.digitalTouchPoint?.category?.subCategory || Flow.BOOKING,
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.site,
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.airlineCode,
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.bookingFlow,
      this.digitalTouchpointData.digitalTouchPoint?.touchpointInfo.currency
    );
    this.analyticsService.setSubcategoryInDigitalTouchpoint(Flow.BOOKING);
  }

  private async getOfficeIDFromStore() {
    const guestOfficeId = this.tokenParametersService.getOfficeIdFromStore();
    return await guestOfficeId();
  }

  /**
   * Set the language in the DOM on language change
   * until https://github.com/ngx-translate/core/issues/565 is implemented
   */
  private setDOMLanguage() {
    this.subscriptions.push(
      this.localizationService.getTranslateService().onLangChange.subscribe((params: LangChangeEvent) => {
        this.analyticsService.setLangInDigitalTouchpoint(getMainLangFromLocale(params.lang));
        if (this.document.documentElement) {
          this.document.documentElement.lang = params.lang;
        }
        this.dynamicLocaleService.setLocale(getDateFnsLocale(params.lang));
      })
    );
    // Set initial locale for DateAdapter since configure() fires onLangChange before this subscription
    this.dynamicLocaleService.setLocale(getDateFnsLocale(this.localizationService.getCurrentLanguage()));
  }

  public ngOnInit() {
    this.setDOMLanguage();

    this.blockingReasons$ = this.store.pipe(
      select(selectRoutingGuardEntitiesBlockingReasons)
    );

    this.loadingContext$ = this.store.select(selectLoadingContext);

    this.subscriptions.push(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationStart)
      ).subscribe(() => {
        // Set the T0 for TTI computation when navigation starts
        this.analyticsUtilityService.setPageStartTime();
      }),
      this.textDirectionService.onLangChangeSubscription(),
      this.isPending$.pipe(debounceTime(1000), filter((isPending) => !isPending)).subscribe(() => this.store.dispatch(ClearLoadingContext()))
    );

    this.setupRulesets();
  }

  /**
   * Triggered when a route is activated. This callback is used
   * for downloading all the lazy chunks like the devtools modules
   * and the lazy components rendered throught the lazy outlet
   */
  public onRouteActivated() {
    if (!this.firstRouteActivated) {
      this.firstRouteActivated = true;
      this.monitorRouterStatus$.next(true);
      this.showDebugPanel = this.appInitializationService.isTraceEnabled();
      this.appInitializationService.initializeDevTools();
    }
  }

  public ngOnDestroy() {
    // clean the subscriptions
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
